import { useEffect, useState } from 'react'
import { MotionConfig } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import Balk from './onderdelen/Balk'
import Poort from './onderdelen/Poort'
import Overzicht from './onderdelen/Overzicht'
import CursusScherm from './onderdelen/CursusScherm'
import { cursusMet } from './cursussen'
import {
  afmelden, herbeginnen, moduleAf, start, toetsGedaan, useStaat, voortgangVan,
} from './lib/staat'

/* ===========================================================================
 *  De academie
 *
 *  Twee schermen, en een adresbalk die klopt
 *  -----------------------------------------
 *
 *  Er zit geen router-bibliotheek in. Er zijn twee schermen -- het overzicht
 *  en een cursus -- en daar is de geschiedenis-API van de browser ruim
 *  genoeg voor. Wat het oplevert is dat /cursus/ai-gebruiken een adres is dat
 *  je kunt doorsturen en dat de terugknop van de browser doet wat hij hoort
 *  te doen.
 *
 *  De Worker weet hiervan: alles wat geen bestand is krijgt index.html terug
 *  (not_found_handling in wrangler.jsonc), dus zo'n adres werkt ook als
 *  iemand het rechtstreeks opent.
 * =========================================================================== */

export default function App() {
  const staat = useStaat()
  const [pad, setPad] = useState(() => window.location.pathname)
  const [fout, setFout] = useState<string | null>(null)

  /* --- opstarten --- */
  useEffect(() => {
    /*
     * De foutcode uit de adresbalk halen en hem daarna weghalen.
     *
     * Blijft hij staan, dan komt bij elke verversing dezelfde melding terug
     * -- ook nadat het inloggen gelukt is. En een melding die niet meer waar
     * is, is erger dan geen melding.
     */
    const p = new URLSearchParams(window.location.search)
    const f = p.get('fout')
    if (f) {
      setFout(f)
      window.history.replaceState(null, '', window.location.pathname)
    }
    void start()
  }, [])

  /* --- de terugknop van de browser --- */
  useEffect(() => {
    function terug() { setPad(window.location.pathname) }
    window.addEventListener('popstate', terug)
    return () => window.removeEventListener('popstate', terug)
  }, [])

  function ga(naar: string) {
    if (naar === window.location.pathname) return
    window.history.pushState(null, '', naar)
    setPad(naar)
    window.scrollTo({ top: 0 })
  }

  /* ---------------------------------------------------------------- *
   *  Wat er te zien is
   * ---------------------------------------------------------------- */

  let scherm
  if (staat.bezig) {
    scherm = (
      <div className="leeg">
        <Loader2 size={24} className="draai" />
        <span>Even kijken wie je bent…</span>
      </div>
    )
  } else if (!staat.ik) {
    scherm = <Poort fout={fout} />
  } else {
    const id = pad.startsWith('/cursus/') ? pad.slice('/cursus/'.length) : null
    const cursus = id ? cursusMet(id) : undefined

    if (id && !cursus) {
      scherm = (
        <div className="leeg">
          <span>Die cursus bestaat niet (meer).</span>
          <button className="knop" onClick={() => ga('/')}>Naar het overzicht</button>
        </div>
      )
    } else if (cursus) {
      scherm = (
        <CursusScherm
          cursus={cursus}
          voortgang={voortgangVan(cursus.id, cursus.versie)}
          naam={staat.ik.naam}
          opModuleAf={(moduleId) => moduleAf(cursus.id, cursus.versie, moduleId)}
          opToets={(score, geslaagd) => toetsGedaan(cursus.id, cursus.versie, score, geslaagd)}
          opOpnieuw={() => herbeginnen(cursus.id, cursus.versie)}
          opTerug={() => ga('/')}
        />
      )
    } else {
      scherm = (
        <Overzicht
          ik={staat.ik}
          voortgang={staat.voortgang}
          bewaard={staat.bewaard}
          opCursus={(cursusId) => ga(`/cursus/${cursusId}`)}
        />
      )
    }
  }

  return (
    /*
     * De keuze "rustige beweging" van het toestel geldt ook voor wat
     * framer-motion doet. De CSS-regel in theme.css dekt alleen animaties in
     * CSS; alles wat hier met JavaScript beweegt gaat hierlangs.
     */
    <MotionConfig reducedMotion="user">
      <div className="blad">
        <Balk
          ik={staat.ik}
          opNaarHuis={() => ga('/')}
          opAfmelden={() => { void afmelden() }}
        />
        {/* Geen AnimatePresence eromheen: de schermen zijn zelf geen
            motion-onderdelen, dus er valt niets uit te animeren. Wat er wél
            beweegt, beweegt binnen het scherm zelf. */}
        {scherm}
      </div>
    </MotionConfig>
  )
}
